import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStrings } from '../../i18n';
import { adminAPI } from '../../services/adminService';
import { CartToastContainer, showCartToast } from '../../components/CartToast';

export default function AdminDashboard() {
  const strings = useStrings();
  const { getIdToken } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const idToken = await getIdToken();
        if (!idToken) {
          throw new Error('No ID token available');
        }
        
        const response = await adminAPI.dashboard(idToken);
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        
        const data = await response.json();
        setMetrics(data);
      } catch (err) {
        setError(err?.message || 'Unknown error');
        showCartToast(`Error: ${err?.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [getIdToken]);

  if (loading) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20">
        <div className="text-center">
          <p className="text-bodyText">{strings.admin?.loading || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p>{strings.admin?.error || 'Error loading dashboard'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-primaryBg min-h-screen">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20 fade-section show">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-heading text-headingText mb-2">
            {strings.admin?.dashboard || 'Admin Dashboard'}
          </h1>
          <p className="text-bodyText text-lg">
            {strings.admin?.dashboardSubtitle || 'Manage your Panch Tatva operations'}
          </p>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {/* Total Revenue */}
          <div className="bg-cardBg rounded-2xl shadow-card p-6 border border-secondary/20">
            <p className="text-textSecondary text-sm mb-2">{strings.admin?.totalRevenue || 'Total Revenue'}</p>
            <p className="text-3xl font-heading text-headingText">
              ₹{metrics?.metrics?.totalRevenue?.toLocaleString('en-IN') || '0'}
            </p>
            <p className="text-textSecondary text-xs mt-2">
              {strings.admin?.thisMonth || 'This month'}
            </p>
          </div>

          {/* Total Orders */}
          <div className="bg-cardBg rounded-2xl shadow-card p-6 border border-secondary/20">
            <p className="text-textSecondary text-sm mb-2">{strings.admin?.totalOrders || 'Total Orders'}</p>
            <p className="text-3xl font-heading text-headingText">
              {metrics?.metrics?.totalOrders || '0'}
            </p>
            <p className="text-textSecondary text-xs mt-2">
              {strings.admin?.allTime || 'All time'}
            </p>
          </div>

          {/* Total Customers */}
          <div className="bg-cardBg rounded-2xl shadow-card p-6 border border-secondary/20">
            <p className="text-textSecondary text-sm mb-2">{strings.admin?.totalCustomers || 'Total Customers'}</p>
            <p className="text-3xl font-heading text-headingText">
              {metrics?.metrics?.totalCustomers || '0'}
            </p>
            <p className="text-textSecondary text-xs mt-2">
              {strings.admin?.active || 'Active'}
            </p>
          </div>

          {/* Pending Actions */}
          <div className="bg-cardBg rounded-2xl shadow-card p-6 border border-yellow-200">
            <p className="text-textSecondary text-sm mb-2">{strings.admin?.pendingActions || 'Pending Actions'}</p>
            <p className="text-3xl font-heading text-yellow-600">
              {(metrics?.metrics?.pendingOrders || 0) + (metrics?.metrics?.openReconciliationIssues || 0)}
            </p>
            <p className="text-textSecondary text-xs mt-2">
              {strings.admin?.requiresAttention || 'Requires attention'}
            </p>
          </div>
        </div>

        {/* Management Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {/* Products Management */}
          <Link to="/admin/products" className="group">
            <div className="bg-cardBg rounded-2xl shadow-card p-8 hover:shadow-lg transition-shadow duration-300 h-full border border-secondary/10 group-hover:border-secondary/30">
              <div className="mb-6">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl">📦</span>
                </div>
                <h3 className="text-xl font-heading text-headingText">
                  {strings.admin?.products || 'Products'}
                </h3>
              </div>
              <p className="text-bodyText text-sm mb-6">
                {strings.admin?.manageProducts || 'Add, edit, delete products and manage inventory'}
              </p>
              <div className="flex items-center text-secondary text-sm font-medium group-hover:translate-x-1 transition-transform">
                {strings.admin?.manage || 'Manage'} →
              </div>
            </div>
          </Link>

          {/* Orders Management */}
          <Link to="/admin/orders" className="group">
            <div className="bg-cardBg rounded-2xl shadow-card p-8 hover:shadow-lg transition-shadow duration-300 h-full border border-secondary/10 group-hover:border-secondary/30">
              <div className="mb-6">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl">📋</span>
                </div>
                <h3 className="text-xl font-heading text-headingText">
                  {strings.admin?.orders || 'Orders'}
                </h3>
              </div>
              <p className="text-bodyText text-sm mb-6">
                {strings.admin?.manageOrders || 'Track orders, process refunds, create manual orders'}
              </p>
              <div className="flex items-center text-secondary text-sm font-medium group-hover:translate-x-1 transition-transform">
                {strings.admin?.manage || 'Manage'} →
              </div>
            </div>
          </Link>

          {/* Customers Management */}
          <Link to="/admin/customers" className="group">
            <div className="bg-cardBg rounded-2xl shadow-card p-8 hover:shadow-lg transition-shadow duration-300 h-full border border-secondary/10 group-hover:border-secondary/30">
              <div className="mb-6">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl">👥</span>
                </div>
                <h3 className="text-xl font-heading text-headingText">
                  {strings.admin?.customers || 'Customers'}
                </h3>
              </div>
              <p className="text-bodyText text-sm mb-6">
                {strings.admin?.manageCustomers || 'View customer details and order history'}
              </p>
              <div className="flex items-center text-secondary text-sm font-medium group-hover:translate-x-1 transition-transform">
                {strings.admin?.manage || 'Manage'} →
              </div>
            </div>
          </Link>

          {/* Finance Management */}
          <Link to="/admin/finance" className="group">
            <div className="bg-cardBg rounded-2xl shadow-card p-8 hover:shadow-lg transition-shadow duration-300 h-full border border-secondary/10 group-hover:border-secondary/30">
              <div className="mb-6">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl">💰</span>
                </div>
                <h3 className="text-xl font-heading text-headingText">
                  {strings.admin?.finance || 'Finance'}
                </h3>
              </div>
              <p className="text-bodyText text-sm mb-6">
                {strings.admin?.manageFinance || 'View transactions, P&L reports, accounting'}
              </p>
              <div className="flex items-center text-secondary text-sm font-medium group-hover:translate-x-1 transition-transform">
                {strings.admin?.manage || 'Manage'} →
              </div>
            </div>
          </Link>

          {/* Admin Users Management */}
          <Link to="/admin/users" className="group">
            <div className="bg-cardBg rounded-2xl shadow-card p-8 hover:shadow-lg transition-shadow duration-300 h-full border border-secondary/10 group-hover:border-secondary/30">
              <div className="mb-6">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl">🔐</span>
                </div>
                <h3 className="text-xl font-heading text-headingText">
                  {strings.admin?.manageAdmins || 'Manage Admins'}
                </h3>
              </div>
              <p className="text-bodyText text-sm mb-6">
                {strings.admin?.adminUsers || 'View, promote, and manage admin users'}
              </p>
              <div className="flex items-center text-secondary text-sm font-medium group-hover:translate-x-1 transition-transform">
                {strings.admin?.manage || 'Manage'} →
              </div>
            </div>
          </Link>

          {/* Shipment Monitor */}
          <Link to="/admin/shipments" className="group">
            <div className="bg-cardBg rounded-2xl shadow-card p-8 hover:shadow-lg transition-shadow duration-300 h-full border border-secondary/10 group-hover:border-secondary/30">
              <div className="mb-6">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl">🚚</span>
                </div>
                <h3 className="text-xl font-heading text-headingText">
                  {strings.adminMonitor?.navTitle || 'Shipment Monitor'}
                </h3>
              </div>
              <p className="text-bodyText text-sm mb-6">
                {strings.adminMonitor?.navDesc || 'Real-time shipment operations — detect failures, stuck orders, SLA breaches'}
              </p>
              <div className="flex items-center text-secondary text-sm font-medium group-hover:translate-x-1 transition-transform">
                {strings.admin?.manage || 'Manage'} →
              </div>
            </div>
          </Link>

          {/* Reconciliation Monitor */}
          <Link to="/admin/reconciliation-monitor" className="group">
            <div className="bg-cardBg rounded-2xl shadow-card p-8 hover:shadow-lg transition-shadow duration-300 h-full border border-secondary/10 group-hover:border-secondary/30">
              <div className="mb-6">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl">🔍</span>
                </div>
                <h3 className="text-xl font-heading text-headingText">
                  {strings.reconciliationMonitor?.navTitle || 'Reconciliation Monitor'}
                </h3>
              </div>
              <p className="text-bodyText text-sm mb-6">
                {strings.reconciliationMonitor?.navDesc || 'Live monitoring of data integrity — orphaned payments, stuck orders, failed reconciliations'}
              </p>
              <div className="flex items-center text-secondary text-sm font-medium group-hover:translate-x-1 transition-transform">
                {strings.admin?.manage || 'Manage'} →
              </div>
            </div>
          </Link>

          {/* System Health */}
          <div className="bg-cardBg rounded-2xl shadow-card p-8 border border-secondary/10">
            <div className="mb-6">
              <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl">⚙️</span>
              </div>
              <h3 className="text-xl font-heading text-headingText">
                {strings.admin?.systemHealth || 'System Health'}
              </h3>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-bodyText">{strings.admin?.razorpay || 'Razorpay'}</span>
                <span className={`px-3 py-1 rounded-full text-white text-xs font-medium ${
                  metrics?.systemHealth?.razorpayConnected ? 'bg-green-600' : 'bg-red-600'
                }`}>
                  {metrics?.systemHealth?.razorpayConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-bodyText">{strings.admin?.database || 'Database'}</span>
                <span className={`px-3 py-1 rounded-full text-white text-xs font-medium ${
                  metrics?.systemHealth?.databaseStatus === 'connected' ? 'bg-green-600' : 'bg-red-600'
                }`}>
                  {metrics?.systemHealth?.databaseStatus === 'connected' ? 'Connected' : 'Error'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Products Section */}
        {metrics?.topProducts && metrics.topProducts.length > 0 && (
          <div className="bg-cardBg rounded-2xl shadow-card p-8 border border-secondary/10 mb-12">
            <h2 className="text-2xl font-heading text-headingText mb-6">
              {strings.admin?.topProducts || 'Top Products'}
            </h2>
            <div className="space-y-4">
              {metrics.topProducts.map((product, idx) => (
                <div key={product.productId} className="flex items-center justify-between pb-4 border-b border-secondary/10 last:border-0">
                  <div className="flex-1">
                    <p className="font-medium text-headingText">{product.name}</p>
                    <p className="text-sm text-textSecondary">{product.element}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-headingText">{product.unitsSold} {strings.admin?.units || 'units'}</p>
                    <p className="text-sm text-secondary">₹{product.revenue.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Orders Section */}
        {metrics?.recentOrders && metrics.recentOrders.length > 0 && (
          <div className="bg-cardBg rounded-2xl shadow-card p-8 border border-secondary/10">
            <h2 className="text-2xl font-heading text-headingText mb-6">
              {strings.admin?.recentOrders || 'Recent Orders'}
            </h2>
            <div className="space-y-4">
              {metrics.recentOrders.map((order) => (
                <Link key={order._id} to={`/admin/orders?search=${order._id}`} className="group">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-primaryBg hover:bg-surface transition-colors duration-200">
                    <div className="flex-1">
                      <p className="font-medium text-headingText group-hover:text-secondary transition-colors">
                        {order.orderId}
                      </p>
                      <p className="text-sm text-bodyText">{order.customerEmail}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-headingText">₹{order.amount.toLocaleString('en-IN')}</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium text-white mt-1 ${
                        order.status === 'delivered' ? 'bg-green-600' :
                        order.status === 'pending_payment' ? 'bg-yellow-600' :
                        order.status === 'cancelled' ? 'bg-red-600' :
                        'bg-blue-600'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

      <CartToastContainer />
      </div>
    </div>
  );
}
